import { UnifiedEvent } from '../types/common';

/**
 * Trouve l'événement le plus proche en date par rapport à aujourd'hui
 * Priorise les événements à venir, puis les plus récents dans le passé
 */
export const findClosestEventByDate = (events: UnifiedEvent[]): UnifiedEvent | null => {
  if (events.length === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset à minuit pour comparer uniquement les dates

  // Séparer les événements avec et sans date
  const eventsWithDate = events.filter(event => event.date);

  if (eventsWithDate.length === 0) {
    // Si aucun événement n'a de date, retourner le premier événement disponible
    return events[0] || null;
  }

  // Convertir les dates et calculer les différences
  const eventsByDistance = eventsWithDate.map(event => {
    let eventDate: Date;
    
    if (event.date instanceof Date) {
      eventDate = new Date(event.date);
    } else if (typeof event.date === 'string') {
      eventDate = new Date(event.date);
    } else {
      return null;
    }
    
    // Si la date est invalide, ignorer cet événement
    if (isNaN(eventDate.getTime())) {
      return null;
    }
    
    eventDate.setHours(0, 0, 0, 0);
    
    // Calculer la différence en jours
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return {
      event,
      eventDate,
      diffDays,
      isUpcoming: diffDays >= 0,
      isPast: diffDays < 0
    };
  }).filter(item => item !== null);

  if (eventsByDistance.length === 0) {
    return events[0] || null;
  }

  // Trier les événements :
  // 1. Événements à venir (diffDays >= 0) par ordre chronologique
  // 2. Événements passés (diffDays < 0) par ordre chronologique inverse (le plus récent d'abord)
  eventsByDistance.sort((a, b) => {
    if (a!.isUpcoming && b!.isUpcoming) {
      // Deux événements à venir : le plus proche en premier
      return a!.diffDays - b!.diffDays;
    } else if (a!.isPast && b!.isPast) {
      // Deux événements passés : le plus récent en premier
      return b!.diffDays - a!.diffDays;
    } else if (a!.isUpcoming && b!.isPast) {
      // Événement à venir prioritaire sur événement passé
      return -1;
    } else {
      // Événement à venir prioritaire sur événement passé
      return 1;
    }
  });

  eventsByDistance.forEach((item, index) => {
    const status = item!.isUpcoming ? '🔜 À venir' : '✅ Passé';
  });

  return eventsByDistance[0]!.event;
};

/**
 * Vérifie s'il faut sélectionner automatiquement l'événement le plus proche
 * Retourne true si c'est la première connexion ou si aucun événement n'est sélectionné
 */
export const shouldAutoSelectClosestEvent = (
  currentEvent: UnifiedEvent | null,
  allEvents: UnifiedEvent[],
  userId: string
): boolean => {
  // Si aucun événement n'est disponible, pas besoin de sélectionner
  if (allEvents.length === 0) return false;
  
  // Si un événement est déjà sélectionné, ne pas changer
  if (currentEvent) return false;
  
  // Vérifier s'il y a déjà eu une sélection manuelle stockée
  const hasManualSelection = localStorage.getItem(`hasManualEventSelection_${userId}`);
  if (hasManualSelection) return false;
  
  // Si c'est la première fois ou si aucun événement n'est sélectionné, sélectionner automatiquement
  return true;
};

/**
 * Marque qu'une sélection manuelle d'événement a été effectuée
 */
export const markManualEventSelection = (userId: string): void => {
  localStorage.setItem(`hasManualEventSelection_${userId}`, 'true');
};

/**
 * Efface la marque de sélection manuelle (utile lors de la déconnexion)
 */
export const clearManualEventSelection = (userId: string): void => {
  localStorage.removeItem(`hasManualEventSelection_${userId}`);
}; 